import type { PerspectiveCamera, Vector3 } from 'three';
import { Vector3 as Vec3 } from 'three';

interface FloatingNumber {
  el: HTMLElement;
  world: Vec3;
  life: number; // seconds remaining
}

const LIFE_SEC = 0.8;
const RISE_UNITS = 70; // world-space float-up over the number's life

/**
 * Floating damage numbers (spec 0026): pooled DOM spans projected from world
 * space each frame; they rise and fade over ~0.8 s. Render-side only — spawned
 * from drained DamageEvents, never from inside the sim.
 */
export class DamageNumbers {
  private readonly live: FloatingNumber[] = [];
  private readonly pool: HTMLElement[] = [];
  private readonly scratch = new Vec3();

  constructor(private readonly root: HTMLElement) {}

  spawn(worldPos: Vector3, amount: number, opts?: { lethal?: boolean }): void {
    const el = this.pool.pop() ?? this.makeEl();
    el.textContent = `${Math.round(amount)}`;
    el.classList.toggle('lethal', opts?.lethal === true);
    el.style.display = 'block';
    this.live.push({ el, world: new Vec3().copy(worldPos), life: LIFE_SEC });
  }

  /** Call once per rendered frame with the active camera. */
  update(camera: PerspectiveCamera, dt: number): void {
    for (let i = this.live.length - 1; i >= 0; i--) {
      const n = this.live[i];
      if (!n) continue;
      n.life -= dt;
      if (n.life <= 0) {
        n.el.style.display = 'none';
        this.pool.push(n.el);
        this.live.splice(i, 1);
        continue;
      }
      const progress = 1 - n.life / LIFE_SEC;
      this.scratch.copy(n.world);
      this.scratch.y += RISE_UNITS * progress;
      this.scratch.project(camera);
      if (this.scratch.z > 1 || this.scratch.z < -1) {
        n.el.style.display = 'none'; // behind the camera
        continue;
      }
      n.el.style.display = 'block';
      n.el.style.left = `${((this.scratch.x + 1) / 2) * window.innerWidth}px`;
      n.el.style.top = `${((1 - this.scratch.y) / 2) * window.innerHeight}px`;
      n.el.style.opacity = `${Math.min(1, n.life / (LIFE_SEC * 0.5))}`;
    }
  }

  private makeEl(): HTMLElement {
    const el = document.createElement('span');
    el.className = 'damage-number';
    this.root.appendChild(el);
    return el;
  }
}
